'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import { isCustomObjectReport } from 'reporting-data/lib/customObjects';
import { isReportGrouping } from 'reporting-data/report/reportGetters';
import { hasSaveActionSupportedDataType } from '../../saveReport/hasSaveActionSupportedDataType';
import { hasSufficientReportViewPermission, isCustom, isCommercePaymentsReport, isContactJourney, isDealJourney } from '../../../lib/report-logic';
import { DOWNGRADE_ARTICLE_URL, getUpgradeUrl } from '../../../lib/linkUrls';
import { useCustomReportCount, useReportCounts, useStandardReportCount } from '../../../data/hooks/useReportCounts';
import { useHasFunnelJourneyAccess } from '../../../access/scopes/hooks/useHasFunnelJourneyAccess';
import { useHasCustomObjectAccess, useHasCustomReportingAccess, useHasReportingCreateAndOwnAccess, useHasPaymentsReportingAccess, useHasMarketingJourneyAccess, useHasSalesJourneyAccess } from '../../../access/scopes/hooks/useHasScope';
import useCustomReportLimit from '../../../access/limits/hooks/useCustomReportLimit';
import useStandardReportLimit from '../../../access/limits/hooks/useStandardReportLimit';
import useUserInfo from '../../../access/useUserInfo';
import { CREATE_NEW_REPORT, CLONE_EXISTING_REPORT, SAVE_UPDATES_AS_NEW } from '../../saveReport/saveNewReportTypes';
import { INSUFFICIENT_ASSET_PERMISSION_LEVEL, INSUFFICIENT_REPORTING_PERMISSION_LEVEL, LACKS_CUSTOM_OBJECT_ACCESS, LACKS_CUSTOM_REPORTING, LACKS_FIELD_ACCESS, LACKS_MARKETING_JOURNEY_REPORTING, LACKS_PAYMENTS_REPORTING_ACCESS, LACKS_SALES_JOURNEY_REPORTING, LACKS_SNOWFLAKE_TEMPLATE_ACCESS, UNSUPPORTED_REPORT_TYPE, WILL_EXCEED_CUSTOM_REPORT_LIMIT, WILL_EXCEED_STANDARD_REPORT_LIMIT, UNSUPPORTED_REPORT_GROUPING } from '../../problems';
import { REPORT_BUILDER_UPGRADE_KEYS, REPORT_LIMIT_UPGRADE_KEYS } from '../upgradePoints/upgradePointKeys';
import { DATA_FETCHING_ERROR_PROBLEM_AND_MESSAGE, LOADING_PROBLEM_AND_MESSAGE } from '../dataProblemsAndMessages';
import { getRestrictedFieldLevelProperties } from '../../../data/schemas/reportSchema';
import RestrictedFieldsProblemMessage from '../RestrictedFieldsProblemMessage';
import { useCheckSnowflakeTemplateAccess } from '../../../access/hooks/useCheckSnowflakeTemplateAccess';
import { jsx as _jsx } from "react/jsx-runtime";
const useCreateNewReportProblem = ({
  report,
  saveUse = CREATE_NEW_REPORT,
  shouldBecomeCustom = false,
  upgradeLocation //one of UPGRADE_POINT_LOCATIONS in upgradePointKeys file
} = {}) => {
  const {
    loading: userInfoLoading,
    error: userInfoError
  } = useUserInfo();
  const {
    loading: reportCountsLoading,
    error: reportCountsError
  } = useReportCounts();
  const customReportCount = useCustomReportCount();
  const customReportLimit = useCustomReportLimit();
  const hasCustomReportingAccess = useHasCustomReportingAccess();
  const hasCustomObjectAccess = useHasCustomObjectAccess();
  const isCustomReport = report && isCustom(report);
  const standardReportCount = useStandardReportCount();
  const standardReportLimit = useStandardReportLimit();
  const hasReportingCreateAndOwnAccess = useHasReportingCreateAndOwnAccess();
  const hasPaymentsReportingAccess = useHasPaymentsReportingAccess();
  const hasMarketingJourneyAccess = useHasMarketingJourneyAccess();
  const hasSalesJourneyAccess = useHasSalesJourneyAccess();
  const hasFunnelJourneyAccess = useHasFunnelJourneyAccess();
  const lacksSnowflakeTemplateAccess = useCheckSnowflakeTemplateAccess(report);
  const restrictedFieldLevelProperties = report ? getRestrictedFieldLevelProperties(report) : [];
  const isCloneCustomReport = (saveUse === CLONE_EXISTING_REPORT || saveUse === SAVE_UPDATES_AS_NEW) && isCustomReport;
  const isCreateCustomReport = saveUse === CREATE_NEW_REPORT && shouldBecomeCustom;
  const willIncreaseCustomReportCount = isCreateCustomReport || isCloneCustomReport;
  const willIncreaseStandardReportCount = !(isCustomReport || shouldBecomeCustom);
  const willExceedCustomLimit = willIncreaseCustomReportCount && customReportCount >= customReportLimit;
  const willExceedStandardLimit = willIncreaseStandardReportCount && standardReportCount >= standardReportLimit;
  if (userInfoLoading || reportCountsLoading) {
    return LOADING_PROBLEM_AND_MESSAGE;
  } else if (userInfoError || reportCountsError) {
    return DATA_FETCHING_ERROR_PROBLEM_AND_MESSAGE;
  } else if (isContactJourney(report) && willIncreaseCustomReportCount && !hasMarketingJourneyAccess && !hasFunnelJourneyAccess) {
    return {
      problem: LACKS_MARKETING_JOURNEY_REPORTING,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.save.button.tooltip.lacksMarketingJourneyReporting"
      })
    };
  } else if (isDealJourney(report) && willIncreaseCustomReportCount && !hasSalesJourneyAccess && !hasFunnelJourneyAccess) {
    return {
      problem: LACKS_SALES_JOURNEY_REPORTING,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.save.button.tooltip.lacksSalesJourneyReporting"
      })
    };
  } else if (willIncreaseCustomReportCount && !hasCustomReportingAccess) {
    return {
      problem: LACKS_CUSTOM_REPORTING,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.save.button.tooltip.lacksCustomReporting"
      }),
      upgradeKey: REPORT_BUILDER_UPGRADE_KEYS[upgradeLocation]
    };
  } else if (willExceedCustomLimit) {
    return {
      problem: WILL_EXCEED_CUSTOM_REPORT_LIMIT,
      disabledMessage: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "reporting-action-components.actions.save.button.tooltip.willExceedCustomReportLimit_jsx",
        elements: {
          UILink
        },
        options: {
          href: getUpgradeUrl(),
          limit: customReportLimit
        }
      }),
      upgradeKey: REPORT_LIMIT_UPGRADE_KEYS[upgradeLocation]
    };
  } else if (willExceedStandardLimit) {
    return {
      problem: WILL_EXCEED_STANDARD_REPORT_LIMIT,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.save.button.tooltip.willExceedStandardReportLimit",
        options: {
          limit: standardReportLimit
        }
      })
    };
  } else if (saveUse === CREATE_NEW_REPORT && restrictedFieldLevelProperties.length) {
    return {
      problem: LACKS_FIELD_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(RestrictedFieldsProblemMessage, {
        message: 'reporting-action-components.actions.save.button.tooltip.restrictedFieldLevelPermission',
        restrictedFieldLevelProperties: restrictedFieldLevelProperties
      })
    };
  } else if (saveUse === CREATE_NEW_REPORT && isCommercePaymentsReport({
    report
  }) && !hasPaymentsReportingAccess) {
    return {
      problem: LACKS_PAYMENTS_REPORTING_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.save.button.tooltip.lacksPaymentsReportingAccess"
      })
    };
  } else if (saveUse === CREATE_NEW_REPORT && lacksSnowflakeTemplateAccess) {
    return {
      problem: LACKS_SNOWFLAKE_TEMPLATE_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.save.button.tooltip.lacksSnowflakeTemplateAccess"
      })
    };
  } else if (report && !hasSaveActionSupportedDataType(report)) {
    return {
      problem: UNSUPPORTED_REPORT_TYPE,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.save.button.tooltip.unsupportedReportType"
      })
    };
  } else if (!hasReportingCreateAndOwnAccess) {
    return {
      problem: INSUFFICIENT_REPORTING_PERMISSION_LEVEL,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.save.button.tooltip.insufficientReportingPermission"
      })
    };
  } else if (report) {
    if ((saveUse === CLONE_EXISTING_REPORT || saveUse === SAVE_UPDATES_AS_NEW) && !hasSufficientReportViewPermission(report)) {
      return {
        problem: INSUFFICIENT_ASSET_PERMISSION_LEVEL,
        disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.save.button.tooltip.insufficientAssetPermission"
        })
      };
    } else if ((saveUse === CLONE_EXISTING_REPORT || saveUse === SAVE_UPDATES_AS_NEW) && restrictedFieldLevelProperties.length) {
      return {
        problem: LACKS_FIELD_ACCESS,
        disabledMessage: /*#__PURE__*/_jsx(RestrictedFieldsProblemMessage, {
          message: 'reporting-action-components.actions.save.button.tooltip.restrictedFieldLevelPermissionClone',
          restrictedFieldLevelProperties: restrictedFieldLevelProperties
        })
      };
    } else if ((saveUse === CLONE_EXISTING_REPORT || saveUse === SAVE_UPDATES_AS_NEW) && isCommercePaymentsReport({
      report
    }) && !hasPaymentsReportingAccess) {
      return {
        problem: LACKS_PAYMENTS_REPORTING_ACCESS,
        disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.save.button.tooltip.lacksPaymentsReportingAccessClone"
        })
      };
    } else if ((saveUse === CLONE_EXISTING_REPORT || saveUse === SAVE_UPDATES_AS_NEW) && lacksSnowflakeTemplateAccess) {
      return {
        problem: LACKS_SNOWFLAKE_TEMPLATE_ACCESS,
        disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.save.button.tooltip.lacksSnowflakeTemplateAccessClone"
        })
      };
    } else if (isCustomObjectReport(report) && !hasCustomObjectAccess) {
      return {
        problem: LACKS_CUSTOM_OBJECT_ACCESS,
        disabledMessage: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "reporting-action-components.actions.save.button.tooltip.lacksCustomObjects_jsx",
          elements: {
            UILink
          },
          options: {
            href: DOWNGRADE_ARTICLE_URL
          }
        })
      };
    } else if (isReportGrouping(report)) {
      return {
        problem: UNSUPPORTED_REPORT_GROUPING,
        disabledMessage: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "reporting-action-components.actions.save.button.tooltip.unsupportedReportGrouping"
        })
      };
    }
  }
  return {
    problem: null,
    disabledMessage: null
  };
};
export default useCreateNewReportProblem;